import WindCreekStorage from "../WindCreekStorage.mjs";
import {
  firstCharacterToUpperCase,
  hideElement,
  query,
  queryAll,
  showElement,
} from '../utils.mjs';
import {getCurTierFromPoints, getNextTierFromPoints} from "./Rewards.mjs";


export default class UserAccount {
  static signInModalElm = query("#wc-rewards-signin-modal");
  static signInModal =
    this.signInModalElm &&
    bootstrap.Modal.getOrCreateInstance(this.signInModalElm);
  static signedInModalElm = query("#wc-rewards-signedin-modal");
  static signedInModal =
    this.signedInModalElm &&
    bootstrap.Modal.getOrCreateInstance(this.signedInModalElm);

  static successfulSignInToast = bootstrap.Toast.getOrCreateInstance(
    query("[data-successful-signin-toast]")
  );
  static successfulSignOutToast = bootstrap.Toast.getOrCreateInstance(
    query("[data-successful-signout-toast]")
  );

  static maintenanceModeEnabled = false;

  static isCacheEnabled() {
    return false;
  }

  static getLoginTypes() {
    return {
      EMAIL_PASSWORD: "EMAIL_PASSWORD",
      REWARDS_NUMBER_PIN: "REWARDS_NUMBER_PIN",
    };
  }

  static login(identity, credential, type) {
    let query = {};
    let loginTypes = this.getLoginTypes();
    if (type === loginTypes.EMAIL_PASSWORD) {
      query = {
        operationName: "MyLogin",
        variables: {
          userCredentials: {
            email: identity,
            password: credential,
          },
        },
        query: `
          mutation MyLogin($userCredentials: UserCredentials!) {
            login(userCredentials: $userCredentials) {
              success
              message
              sessionToken
              acceptedCurrentTermsOfService
              user {
                ...UserFields
              }
            }
          }

          fragment UserFields on User {
            userId
            email
            displayName
            preferredLocation
            preferredLanguage
            avatarUrl
            isLockedOut
            isWallBanned
            creationDate
            roles
          }`,
      };
    } else if (type === loginTypes.REWARDS_NUMBER_PIN) {
      query = {
        operationName: "MyLogin",
        variables: {
          patronAccountNumberAndPin: {
            patronAccountNumber: identity,
            pin: credential,
          },
        },
        query: `
          mutation MyLogin($patronAccountNumberAndPin: PatronAccountNumberAndPin!) {
            patronLoginV2(patronAccountNumberAndPin: $patronAccountNumberAndPin) {
              success
              errorMessage
              sessionToken
            }
          }`,
      };
    } else {
      throw new Error(
        `Unrecognized login type '${type}'. See getLoginTypes() for a list of valid types.`
      );
    }

    return this.windCreekClient.query(query).then((result) => {
      let loginTypes = this.getLoginTypes();

      if (type === loginTypes.EMAIL_PASSWORD) {
        result = result?.data?.login;
      } else if (type === loginTypes.REWARDS_NUMBER_PIN) {
        result = result?.data?.patronLoginV2;
      }

      if (result === undefined) {
        throw new Error(
          "User login failed. Check the network tab for details on the API call."
        );
      }

      if (result?.success) {
        /*
         Check the user's roles to make sure they have the correct account type for this site.
         If they don't, the login call itself will be successful, but it will create issues with other calls, so we need
         to fake an error response here so the UI doesn't finish the login process.
         */
        if (type === loginTypes.EMAIL_PASSWORD) {
          let roles = result?.user?.roles ?? [];
          if (this.isMiamiSite() && !roles.includes('user-session-with-pls-player-id')) {
            return 'MISSING_REQUIRED_ROLE';
          } else if (!this.isMiamiSite() && !roles.includes('user-session-with-patron-account')) {
            return'MISSING_REQUIRED_ROLE';
          }
        }

        WindCreekStorage.setItem("user_auth_token", result.sessionToken);
        WindCreekStorage.setItem("user_login_type", type);

        /*
        In addition to local storage (if available), we need to create a cookie that will get passed to the server on
        each request. This allows our backend logic to know if the user is logged in or not.

        In a separate step, we also save a cookie with their rewards account number (not available at this point) so the
        server has an idea of who they are.

        Note: This can be easily spoofed so it's not used for any real authorization! It's just for improving UX. We're
        intentionally not sending the user's auth token to the server for security reasons. That never leaves the user's
        browser, and it's only used for making calls to the WC API in JS.
        */
        document.cookie =
          "user_logged_in=" +
          encodeURIComponent("true") +
          "; path=/; max-age=31536000;";

        if (result?.acceptedCurrentTermsOfService === false) {
          WindCreekStorage.setItem("user_accept_tos_required", true);
          this.setStatusNeedsToAcceptTOS();
        }
        return result;
      } else {
        // Let the calling code decide how to handle the error
        if (type === loginTypes.EMAIL_PASSWORD) {
          return result?.message || "";
        } else if (type === loginTypes.REWARDS_NUMBER_PIN) {
          return result?.errorMessage || "";
        }
      }
    });
  }

  static logout() {
    if (!this.isLoggedIn()) {
      throw new Error("User is not logged in.");
    }

    let loginTypes = this.getLoginTypes();
    let type = this.getLoginType();
    let query = "";

    if (type === loginTypes.EMAIL_PASSWORD) {
      query = "mutation { logout }";
    } else if (type === loginTypes.REWARDS_NUMBER_PIN) {
      query = "mutation { patronLogout }";
    }

    this.windCreekClient.ignore401 = true;
    return this.windCreekClient.query(query).finally(() => {
      this.clearStorage();

      this.setStatusLoggedOut(true);

      this.windCreekClient.ignore401 = false;

      if (this.onAccountPage()) {
        setTimeout(() => {
          location.href = "/";
        }, 500);
      }

      return true;
    });
  }

  static clearStorage() {
    // This stuff is always stored locally
    WindCreekStorage.removeItem("user_auth_token");
    WindCreekStorage.removeItem("user_login_type");
    WindCreekStorage.removeItem("user_account_number");

    document.cookie =
      "user_logged_in=; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC;";
    document.cookie =
      "user_account_number=; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC;";

    // This stuff is only stored locally if the cache is enabled (so we don't have to make a network request every time)
    if (this.isCacheEnabled()) {
      WindCreekStorage.removeItem("user_account_data");
      WindCreekStorage.removeItem("user_profile_data");
      WindCreekStorage.removeItem("user_rewards");
      WindCreekStorage.removeItem("user_win_loss_statement");
      WindCreekStorage.removeItem("user_hotel_reservations");
      WindCreekStorage.removeItem("user_terms_of_service");
      WindCreekStorage.removeItem("user_accept_tos_required");
      WindCreekStorage.removeItem("prime_perks_prizes");
      WindCreekStorage.removeItem("user_prime_tokens");
      WindCreekStorage.removeItem("user_redeemed_prime_perks");
    }
  }

  static isLoggedIn() {
    this.getExternalAuthToken();
    return WindCreekStorage.exists("user_auth_token");
  }

  /*
   Checks to see if an auth token was passed in the "token" URL parameter. The Wind Creek team uses this parameter when
   redirecting users from their app/site to our site. If the user has already logged in there, this is how they notify
   our site about the existing session (so they don't have to log in again).
   */
  static getExternalAuthToken() {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const urlParamToken = urlSearchParams.get('token');

    if (urlParamToken) {
      WindCreekStorage.setItem("user_auth_token", urlParamToken);
      WindCreekStorage.setItem("user_login_type", this.getLoginTypes().EMAIL_PASSWORD);

      /*
      In addition to local storage (if available), we need to create a cookie that will get passed to the server on
      each request. This allows our backend logic to know if the user is logged in or not.

      In a separate step, we also save a cookie with their rewards account number (not available at this point) so the
      server has an idea of who they are.

      Note: This can be easily spoofed so it's not used for any real authorization! It's just for improving UX. We're
      intentionally not sending the user's auth token to the server for security reasons. That never leaves the user's
      browser, and it's only used for making calls to the WC API in JS.
      */
      document.cookie =
        "user_logged_in=" +
        encodeURIComponent("true") +
        "; path=/; max-age=31536000;";

      return true;
    }

    return false;
  }

  static isOnHighestTier(profileData) {
    // Get the curTier to compare for PLS accounts
    const curTier = getCurTierFromPoints(profileData.basePointsEarned);
    let nextTier;
    let tier = getNextTierFromPoints(profileData.basePointsEarned);
    if(tier !== 'Unknown') {
      nextTier = tier.label;
    } else {
      nextTier = tier;
    }
    // To determine if the user is on the highest tier, we check to see if there's a valid value for the next tier field
    return nextTier === undefined ||
      nextTier === null ||
      nextTier === '' ||
      nextTier === 'Unknown' ||
      (curTier === "Fantasy" && nextTier === "Fantasy");
  }

  static getAuthToken() {
    return WindCreekStorage.getItem("user_auth_token");
  }

  static getLoginType() {
    return WindCreekStorage.getItem("user_login_type");
  }

  static onAccountPage() {
    let accountPageSelectors = [
      "[data-profile-page]",
      "[data-my-rewards-page]",
      "[data-my-reservations-page]",
      "[data-win-loss-statement-page]",
    ];

    let selector = accountPageSelectors.join(",");

    return queryAll(selector).length > 0;
  }

  static forceAuthentication() {
    location.href =
      "/?login=true&redirect=" + encodeURIComponent(location.pathname);
  }

  static isMiamiSite() {
    return WindCreekData?.isMiamiSite ?? false;
  }

  static isMCCSite() {
    return WindCreekData?.propertyURL === '/magic-city-casino';
  }

  static getCMPClubStateFromPLSClubState(clubState) {
    let clubStateMap = {
      magician: 'Play',
      illusionist: 'Escape',
      wizard: 'Delight',
      oracle: 'Fantasy',
    };

    return clubStateMap[clubState];
  }

  static getName(profileData) {
    return (
      profileData.patronInfo.firstName + " " + profileData.patronInfo.lastName
    );
  }

  static getDisplayName(accountData, profileData) {
    return (
      accountData?.displayName ||
      profileData.patronInfo.firstName + " " + profileData.patronInfo.lastName
    );
  }

  static forgotPassword(email) {
    return this.windCreekClient
      .query({
        operationName: "MyForgotPassword",
        variables: {
          email: email,
        },
        query: `
        mutation MyForgotPassword($email: String!) {
          forgotPassword(email: $email) {
            message
          }
        }`,
      })
      .then((result) => {
        result = result?.data?.forgotPassword;

        if (result === undefined) {
          throw new Error(
            "Password reset failed. Check the network tab for details on the API call."
          );
        }

        return result;
      });
  }

  static getAccountData() {
    if (!this.isLoggedIn()) {
      throw new Error("User is not logged in.");
    }

    if (this.isCacheEnabled() && WindCreekStorage.exists("user_account_data")) {
      return Promise.resolve(WindCreekStorage.getItem("user_account_data"));
    } else {
      return this.windCreekClient
        .query(
          `
        {
          user {
            ...UserFields
          }
        }

        fragment UserFields on User {
          userId
          email
          displayName
          preferredLocation
          preferredLanguage
          avatarUrl
          isLockedOut
          isWallBanned
          creationDate
          roles
        }
      `
        )
        .then((result) => {
          result = result?.data?.user;

          if (result === undefined) {
            throw new Error(
              "Unable to get user account data. Check the network tab for details on the API call."
            );
          }

          if (this.isCacheEnabled()) {
            WindCreekStorage.setItem("user_account_data", result);
          }

          return result;
        });
    }
  }

  static getProfile() {
    if (this.isMiamiSite()) {
      return this.getProfileV2();
    } else {
      return this.getProfileV1();
    }
  }

  // This is specifically for CMP accounts (all properties except Miami)
  static getProfileV1() {
    if (!this.isLoggedIn()) {
      throw new Error("User is not logged in.");
    }

    if (this.isCacheEnabled() && WindCreekStorage.exists("user_profile_data")) {
      return Promise.resolve(WindCreekStorage.getItem("user_profile_data"));
    } else {
      return this.windCreekClient
        .query(
          `
        {
          status {
            patronAccountNumber
            patronInfo {
              firstName
              lastName
              email
              birthDate
              rewardsJoinDate
              homePhone
              mobilePhone
              gender
              address1
              address2
              city
              stateProvince
              postalCode
            }
            freePlay
            shoppingDollars
            basePoints
            clubState
            nextClubState
            pointsNeeded
            basePointsEarned
            drawings {
              name
              amount
            }
          }
        }
      `
        )
        .then((result) => {
          result = result?.data?.status;

          if (result === undefined) {
            throw new Error(
              "Unable to get user profile data. Check the network tab for details on the API call."
            );
          }

          WindCreekStorage.setItem(
            "user_account_number",
            result.patronAccountNumber
          );

          /*
        In addition to local storage (if available), we always save the account number in a cookie too so it
        automatically gets sent to the server on each request. This allows our backend logic to access it.

        Note: This can be easily spoofed so it's not used for any real authorization! It's just for improving UX. We're
        intentionally not sending the user's auth token to the server for security reasons. That never leaves the user's
        browser, and it's only used for making calls to the WC API in JS.
        */
          document.cookie =
            "user_account_number=" +
            encodeURIComponent(result.patronAccountNumber) +
            "; path=/; max-age=31536000;";

          if (this.isCacheEnabled()) {
            WindCreekStorage.setItem("user_profile_data", result);
          }

          // if (result.patronAccountNumber === '0971002598794') {
          //   result.basePointsEarned = 600;
          //   // result.pointsNeeded = 100000;
          //   console.log('account data override');
          // } else if (result.patronAccountNumber === '5751010487763') {
          //   result.pointsNeeded = 25000;
          //   console.log('account data override');
          // }

          return result;
        });
    }
  }

  // This is specifically for PLS (Miami) accounts
  static getProfileV2() {
    if (!this.isLoggedIn()) {
      throw new Error("User is not logged in.");
    }

    if (this.isCacheEnabled() && WindCreekStorage.exists("user_profile_data")) {
      return Promise.resolve(WindCreekStorage.getItem("user_profile_data"));
    } else {
      return this.windCreekClient
        .query(
          `
        query {
          statusV2 {
            plsPlayerStatus {
              plsPlayerId
              firstName
              lastName
              dateOfBirth
              gender
              addresses {
                addressType
                street
                postalCode
                city
                state
                country
              }
              phones {
                phoneType
                phoneNumber
              }
              emails
              rewardsJoinDate
              accounts {
                plsAccountNumber
                playerCards {
                  cardNumber
                  cardType
                }
              }
              rewardsTier
              ytdTierPoints
              pointsBalance
              pointsEarnedTowardsNextTierLevel
              pointsRemainingUntilNextTierLevel
              freePlay
              nextRewardTier
            }
          }
        }
      `
        )
        .then((result) => {
          result = result?.data?.statusV2?.plsPlayerStatus;

          if (result === undefined) {
            throw new Error(
              "Unable to get user profile data. Check the network tab for details on the API call."
            );
          }

          /*
          The response structure for the statusV2 query is very different from the response of the normal status query,
          so now we're going to try to map/convert all the fields in this response to the same fields in the other
          response so they match. That way both functions return an object with the same structure.
          */
          if (result.emails) {
            result.email = result.emails[0];
            result.emails.shift();
            result.emailOther = result.emails.length ? result.emails : null;
          }

          result.birthDate = result.birthDate ?? result.dateOfBirth;

          let gender;
          if (result.gender === "male") {
            gender = "M";
          } else if (result.gender === "female") {
            gender = "F";
          } else {
            gender = result.gender;
          }

          let phones = {
            mobile: null,
            home: null,
            other: [],
          };
          result.phones.forEach((phone) => {
            if (phone.phoneType === "mobile") {
              phones.mobile = phone.phoneNumber;
            } else if (phone.phoneType === "home") {
              phones.home = phone.phoneNumber;
            } else {
              phones.other.push({
                phoneType: phone.phoneType,
                phoneNumber: phone.phoneNumber,
              });
            }
          });

          if (result.addresses) {
            result.address1 = result.addresses[0].street[0];
            result.address2 = result.addresses[0].street[1];
            result.city = result.addresses[0].city;
            result.stateProvince = result.addresses[0].state;
            result.postalCode = result.addresses[0].postalCode;

            result.addresses.shift();
            result.addressOther = [];
            result.addresses.forEach((entry) => {
              const address = {
                addressType: entry.addressType,
                address1: entry.street[0],
                address2: entry.street[1],
                city: entry.city,
                stateProvince: entry.state,
                postalCode: entry.postalCode,
              };
              result.addressOther.push(address);
            });
          }

          // TODO: might need to fix the structure of this
          if (result.accounts) {
            result.patronAccountNumber = result.accounts[0].plsAccountNumber;
            result.accountCard = null;
            if (result.accounts[0].playerCards.length) {
              result.accountCard = {
                cardNumber: result.accounts[0].playerCards[0].cardNumber,
                cardType: result.accounts[0].playerCards[0].cardType ?? null,
              };
            }
          }

          result = {
            freePlay: result.freePlay.replace("$", ""),
            nextClubState: firstCharacterToUpperCase(result.nextRewardTier),
            patronAccountNumber: result.patronAccountNumber,
            accountCard: result.accountCard ?? null,
            clubState: firstCharacterToUpperCase(result.rewardsTier),
            email: result.email ?? "not found", // TODO: does this need to here if it's already inside patronInfo like V1
            emailOther: result.emailOther, // TODO: does this need to here if it's already inside patronInfo like V1
            patronInfo: {
              firstName: result.firstName,
              lastName: result.lastName,
              email: result.email ?? "not found",
              emailOther: result?.emailOther ?? [],
              address1: result.address1,
              address2: result.address2,
              city: result.city,
              stateProvince: result.stateProvince,
              postalCode: result.postalCode,
              addressOther: result?.addressOther ?? [],
              gender: gender,
              birthDate: result.dateOfBirth,
              homePhone: phones?.home ?? "not found",
              mobilePhone: phones?.mobile ?? "not found",
              phoneOther: phones?.other ?? [],
              rewardsJoinDate: result.rewardsJoinDate,
              accountCard: result.accountCard ?? null,
            },
            basePoints: result.pointsBalance,
            shoppingDollars: null,
            basePointsEarned: result.pointsEarnedTowardsNextTierLevel,
            drawings: [],
            pointsNeeded: result.pointsRemainingUntilNextTierLevel ?? 0, // This seems to be null on the highest level
          };

          WindCreekStorage.setItem(
            "user_account_number",
            result.patronAccountNumber
          );

          /*
        In addition to local storage (if available), we always save the account number in a cookie too so it
        automatically gets sent to the server on each request. This allows our backend logic to access it.

        Note: This can be easily spoofed so it's not used for any real authorization! It's just for improving UX. We're
        intentionally not sending the user's auth token to the server for security reasons. That never leaves the user's
        browser, and it's only used for making calls to the WC API in JS.
        */
          document.cookie =
            "user_account_number=" +
            encodeURIComponent(result.patronAccountNumber) +
            "; path=/; max-age=31536000;";

          if (this.isCacheEnabled()) {
            WindCreekStorage.setItem("user_profile_data", result);
          }

          // if (result.patronAccountNumber === '3057975747573') {
          //   result.pointsNeeded = 5000;
          //   result.clubState = this.getCMPClubStateFromPLSClubState(result.clubState);
          //   result.nextClubState = this.getCMPClubStateFromPLSClubState(result.nextClubState);
          //   console.log('account data override');
          // } else if (result.patronAccountNumber === '3051240992921') {
          //   result.pointsNeeded = 25000;
          //   result.clubState = "Escape";
          //   result.nextClubState = "Delight";
          //   console.log('account data override');
          // } else if (result.patronAccountNumber === '3055630898041') {
          //   result.basePointsEarned = 600;
          //   // result.pointsNeeded = 100000;
          //   result.clubState = "Delight";
          //   result.nextClubState = "Fantasy";
          //   console.log('account data override');
          // } else if (result.patronAccountNumber === '3057148921181') {
          //   // result.clubState = "Fantasy";
          //   // result.nextClubState = "Unknown";
          //   console.log('account data override');
          // }

            return result;
        });
    }
  }

  static getRewards() {
    return this.getRewardsV2();
    /*if (this.isMiamiSite()) {
      return this.getRewardsV2();
    } else {
      return this.getRewardsV1();
    }*/
  }

  // This is specifically for CMP accounts (all properties except Miami)
  static getRewardsV1() {
    if (!this.isLoggedIn()) {
      throw new Error("User is not logged in.");
    }

    if (this.isCacheEnabled() && WindCreekStorage.exists("user_rewards")) {
      return Promise.resolve(WindCreekStorage.getItem("user_rewards"));
    } else {
      /*
      The patronAssets field (including tickets and wscore) are only available if they log in with a username and
      password (not rewards # and PIN)
      */
      let loginTypes = this.getLoginTypes();
      let type = this.getLoginType();
      let onlineAccountFields = "";

      if (type === loginTypes.EMAIL_PASSWORD) {
        onlineAccountFields = `
          patronAssets {
            gameBalance
            tickets
            wscore
          }
        `;
      }

      return this.windCreekClient
        .query(
          `
        {
          rewardsWithTimeZone {
            rewardTimeZoneHelpText
            patronRewards {
                prizeCode
                prizeName
                prizeType
                startDate
                endDate
                startDateUTC
                endDateUTC
                isEarnRequired
                earnType
                earnOverDays
                earnAmount
                maxPrizesPerPeriod
                redeemed
                promoType
                isNewOffer
                isSoftOffer
                prize {
                  id
                  name
                  category
                  description
                }
            }
          }

          ${onlineAccountFields}
        }
      `
        )
        .then((result) => {
          result = result?.data;

          result.rewardsWithTimeZone.partnerRewards = []; // TODO: need real value

          if (result === undefined) {
            throw new Error(
              "Unable to get user rewards data. Check the network tab for details on the API call."
            );
          }

          if (this.isCacheEnabled()) {
            WindCreekStorage.setItem("user_rewards", result);
          }

          return result;
        });
    }
  }

  // This is specifically for PLS (Miami) accounts
  static getRewardsV2() {
    if (!this.isLoggedIn()) {
      throw new Error("User is not logged in.");
    }

    if (this.isCacheEnabled() && WindCreekStorage.exists("user_rewards")) {
      return Promise.resolve(WindCreekStorage.getItem("user_rewards"));
    } else {
      let fieldsToQuery;
      if (this.isMiamiSite()) {
        fieldsToQuery = `
          plsPrizeAssignments {
            timeZoneHelpText
            prizeAssignments {
              playerId
              originatingOnlinePrize {
                url
              }
              plsPrize {
                name
                description
                allowsPartialRedemption
                prizeType
                amount
                value
                valueInCents
              }
              assignmentState
              remainingValue
              usedValue
              validFromDateTime
              validToDateTime
              dailyValidFromTime
              dailyValidToTime
              validFromDateTimeUTC
              validToDateTimeUTC
              dailyValidFromTimeUTC
              dailyValidToTimeUTC
              validDaysOfWeek
              blackoutDates
              isExpired
              requestId
            }
          }
        `;
      } else {
        fieldsToQuery = `
          patronRewards {
            rewardTimeZoneHelpText
            patronRewards {
              prizeCode
              prizeName
              prizeType
              startDate
              endDate
              startDateUTC
              endDateUTC
              isEarnRequired
              earnType
              earnOverDays
              earnAmount
              maxPrizesPerPeriod
              redeemed
              promoType
              isNewOffer
              isSoftOffer
              prize {
                id
                name
                category
                description
              }
            }
          }
        `;
      }

      return this.windCreekClient
        .query(
          `
        query {
          rewardsWithTimeZoneV2 {

            ${fieldsToQuery}

            partnerRewards {
              partnerRewards {
                patronAccountNumber
                prize {
                  id
                  lastModified
                  templateCode
                  plsPrizeId
                  url
                  category
                  subCategory
                  name
                  locations
                  description
                  tickets
                  startDate
                  startTime
                  endTime
                  validFor
                }
                event {
                  eventType
                  eventDate
                }
                startDate
                endDate
                startDateUTC
                endDateUTC
                amount
                amountInCents
                redeemLink
                redeemCode
              }
            }
          }

          patronAssets {
            gameBalance
            tickets
            wscore
          }
        }
      `
        )
        .then((result) => {
          result = result?.data;

          if (result === undefined) {
            throw new Error(
              "Unable to get user rewards data. Check the network tab for details on the API call."
            );
          }

          /*
          The response structure for the rewardsWithTimeZoneV2 query is very different from the response of the normal
          rewardsWithTimeZone query, so now we're going to try to map/convert all the fields in this response to the
          same fields in the other response so they match. That way both functions return an object with the same structure.
          */
          let rewardTimeZoneHelpText;
          let patronRewards;
          if (this.isMiamiSite()) {
            rewardTimeZoneHelpText = result.rewardsWithTimeZoneV2.plsPrizeAssignments.timeZoneHelpText;
            patronRewards = result.rewardsWithTimeZoneV2.plsPrizeAssignments.prizeAssignments;
          } else {
            rewardTimeZoneHelpText = result.rewardsWithTimeZoneV2.patronRewards.rewardTimeZoneHelpText;
            patronRewards = result.rewardsWithTimeZoneV2.patronRewards.patronRewards;
          }
          result = {
            rewardsWithTimeZone: {
              rewardTimeZoneHelpText: rewardTimeZoneHelpText,
              patronRewards: patronRewards,
              partnerRewards: result.rewardsWithTimeZoneV2?.partnerRewards?.partnerRewards,
            },
            patronAssets: result.patronAssets,
          };

          if (this.isCacheEnabled()) {
            WindCreekStorage.setItem("user_rewards", result);
          }

          return result;
        });
    }
  }

  static getWinLossStatement(year) {
    if (!this.isLoggedIn()) {
      throw new Error("User is not logged in.");
    }

    if (
      this.isCacheEnabled() &&
      WindCreekStorage.exists("user_win_loss_statement")
    ) {
      return Promise.resolve(
        WindCreekStorage.getItem("user_win_loss_statement")
      );
    } else {
      let patronAccountNumber = WindCreekStorage.getItem("user_account_number");

      return this.windCreekClient
        .query({
          operationName: "MyWinLossStatement",
          variables: {
            getWinLoss: {
              patronAccountNumber: patronAccountNumber,
              year: year,
            },
          },
          query: `
          query MyWinLossStatement($getWinLoss: GetWinLoss!) {
            winLossHistory(input: $getWinLoss) {
              properties {
                name
                propertyValue {
                  month
                  value
                }
              }
            }
          }`,
        })
        .then((result) => {
          result = result?.data?.winLossHistory?.properties;

          if (result === undefined) {
            throw new Error(
              "Unable to get win loss statement. Check the network tab for details on the API call."
            );
          }

          if (this.isCacheEnabled()) {
            WindCreekStorage.setItem("user_win_loss_statement", result);
          }

          return result;
        });
    }
  }

  static getHotelReservations() {
    if (!this.isLoggedIn()) {
      throw new Error("User is not logged in.");
    }

    if (
      this.isCacheEnabled() &&
      WindCreekStorage.exists("user_hotel_reservations")
    ) {
      return Promise.resolve(
        WindCreekStorage.getItem("user_hotel_reservations")
      );
    } else {
      return this.windCreekClient
        .query({
          operationName: "MyGetPatronBookings",
          variables: {
            input: {
              /*
            'statusFilters': [
              'RESERVED',
              'PROSPECT',
              'NOSHOW',
              'CANCELED',
              'INHOUSE',
              'CHECKEDOUT',
              'CHANGED',
              'WAITLISTED',
              'PRECHECKEDIN',
              'DUEOUT',
              'DUEIN'
            ],
            'queryDateRange': {
              'startDate': '2018-01-01',
              'endDate': '2022-01-01'
            }
            */
            },
          },
          query: `
          query GetPatronBookings($input: GetBookingsByPatron!) {
            patronBookings(input: $input) {
              bookings {
                id
                reservationId
                hotelCode
                source
                startDate
                endDate
                createdDate
                total
                formattedTotal
                profileId
                firstName
                lastName
                email
                accountNumber
                status
                createdBy {
                  email
                }
                checkedIn
                checkedOut
                allowedCheckInTime
                bookingSource
                rateCode
                roomType
                roomTypeDescription
                owsPromotionCode
                owsCustomReference
                roomNumber
                printRate
                charges {
                  id
                  postingDateLocal
                  description
                  formattedAmount
                }
              }
            }
          }`,
        })
        .then((result) => {
          result = result?.data?.patronBookings?.bookings;

          if (result === undefined) {
            throw new Error(
              "Unable to get hotel reservations. Check the network tab for details on the API call."
            );
          }

          if (this.isCacheEnabled()) {
            WindCreekStorage.setItem("user_hotel_reservations", result);
          }

          return result;
        });
    }
  }

  static getHotelStayPreferenceCategories(propertyCode = null) {
    if (
      this.isCacheEnabled() &&
      WindCreekStorage.exists("user_hotel_stay_preference_categories")
    ) {
      return Promise.resolve(
        WindCreekStorage.getItem("user_hotel_stay_preference_categories")
      );
    } else {
      return this.windCreekClient
        .query({
          variables: {
            input: {
              propertyCode: propertyCode,
            },
          },
          query: `
            query GetCategories($input: HotelStayPreferenceCategoriesInput) {
              hotelStayPreferenceCategories(input: $input) {
                categories {
                  code
                  name
                  description
                  selectionType
                  displayOrder
                  enabled
                  applicablePropertyCodes
                  options {
                    code
                    name
                    description
                    imageUrl
                    displayOrder
                    enabled
                    applicablePropertyCodes
                  }
                }
              }
            }
        `,
        })
        .then((result) => {
          result = result?.data?.hotelStayPreferenceCategories?.categories;

          if (result === undefined) {
            throw new Error(
              "Unable to get hotel stay preference categories. Check the network tab for details on the API call."
            );
          }

          if (this.isCacheEnabled()) {
            WindCreekStorage.setItem("user_hotel_stay_preference_categories", result);
          }

          return result;
        });
    }
  }

  static getUserHotelStayPreferences() {
    if (
      this.isCacheEnabled() &&
      WindCreekStorage.exists("user_hotel_stay_preferences")
    ) {
      return Promise.resolve(
        WindCreekStorage.getItem("user_hotel_stay_preferences")
      );
    } else {
      return this.windCreekClient
        .query(`
          {
            userHotelStayPreferences {
              preferences {
                categoryCode
                optionCode
              }
              priorityCategoryCode
            }
          }
        `)
        .then((result) => {
          result = result?.data?.userHotelStayPreferences;

          if (result === undefined) {
            throw new Error(
              "Unable to get user's hotel stay preferences. Check the network tab for details on the API call."
            );
          }

          if (this.isCacheEnabled()) {
            WindCreekStorage.setItem("user_hotel_stay_preferences", result);
          }

          return result;
        });
    }
  }

  // Example structure for preferences object. This is a full update, so all fields must be sent and will be overwritten.
  /*
    {
      "preferences": [
        { "categoryCode": "AMENITY_SCENT", "optionCode": "SERENIVA" },
        { "categoryCode": "ELEVATOR_PROXIMITY", "optionCode": "NE" },
        { "categoryCode": "FLOOR_HEIGHT", "optionCode": "LF" },
        { "categoryCode": "HYPOALLERGENIC_BEDDING", "optionCode": "ENABLED" }
      ],
      "priorityCategoryCode": "AMENITY_SCENT"
    }
  */
  static saveUserHotelStayPreferences(preferences) {
    return this.windCreekClient
      .query({
        variables: {
          input: preferences
        },
        query: `
          mutation SaveUserHotelStayPreferences($input: SaveUserHotelStayPreferencesInput!) {
            saveUserHotelStayPreferences(input: $input) {
              success
              error
            }
          }
        `,
      })
      .then((result) => {
        result = result?.data?.saveUserHotelStayPreferences;

        if (result === undefined) {
          throw new Error(
            "Unable to save user's hotel stay preferences. Check the network tab for details on the API call."
          );
        }

        return result;
      });
  }

  // Get the list of all available prime perks
  static getPrimePerksPrizes(property) {
    if (!this.isLoggedIn()) {
      throw new Error("User is not logged in.");
    }

    property = this.getPropertySlugFromName(property);

    if (
      this.isCacheEnabled() &&
      WindCreekStorage.exists("prime_perks_prizes")
    ) {
      return Promise.resolve(
        WindCreekStorage.getItem("prime_perks_prizes")
      );
    } else {
      return this.windCreekClient
        .query({
          variables: {
            input: {
              location: property,
            },
          },
          query: `
            query GetPrimePerksPrizes($input: GetPrimePerksPrizesInput!) {
              getPrimePerksPrizes(input: $input) {
                prizes {
                  primePerkId
                  name
                  description
                  wscore
                  primeTokens
                  category
                }
              }
            }`,
        })
        .then((result) => {
          result = result?.data?.getPrimePerksPrizes.prizes;

          if (result === undefined) {
            throw new Error(
              "Unable to get prime perks prizes. Check the network tab for details on the API call."
            );
          }

          if (this.isCacheEnabled()) {
            WindCreekStorage.setItem("prime_perks_prizes", result);
          }

          const hiddenPrizeIDs = [
            "8256",
            "8257",
            "8258",
          ];

          const filteredResult = result.filter(item => !hiddenPrizeIDs.includes(item.primePerkId));

          return filteredResult;
        });
    }
  }

  // Get the current user's prime token balance
  static getPrimeTokens(property) {
    if (!this.isLoggedIn()) {
      throw new Error("User is not logged in.");
    }

    property = this.getPropertySlugFromName(property);

    if (
      this.isCacheEnabled() &&
      WindCreekStorage.exists("user_prime_tokens")
    ) {
      return Promise.resolve(
        WindCreekStorage.getItem("user_prime_tokens")
      );
    } else {
      return this.windCreekClient
        .query({
          variables: {
            input: {
              location: property,
            },
          },
          query: `
            query GetPrimeTokens($input: GetPrimeTokensInput!) {
              getPrimeTokens(input: $input) {
                amount
              }
            }`,
        })
        .then((result) => {
          result = result?.data?.getPrimeTokens.amount;

          if (result === undefined) {
            throw new Error(
              "Unable to get user's prime tokens. Check the network tab for details on the API call."
            );
          }

          if (this.isCacheEnabled()) {
            WindCreekStorage.setItem("user_prime_tokens", result);
          }

          return result;
        });
    }
  }

  // Get the current user's list of redeemed prime perks
  static getRedeemedPrimePerks(property) {
    if (!this.isLoggedIn()) {
      throw new Error("User is not logged in.");
    }

    property = this.getPropertySlugFromName(property);

    if (
      this.isCacheEnabled() &&
      WindCreekStorage.exists("user_redeemed_prime_perks")
    ) {
      return Promise.resolve(
        WindCreekStorage.getItem("user_redeemed_prime_perks")
      );
    } else {
      return this.windCreekClient
        .query({
          variables: {
            input: {
              location: property,
            },
          },
          query: `
            query GetRedeemedPrimePerks($input: GetRedeemedPrimePerksInput!) {
              getRedeemedPrimePerks(input: $input) {
                primePerks {
                  name
                  description
                  beginMonth
                  beginYear
                  throughMonth
                  throughYear
                  prizeCode
                  primePerkAssignmentId
                }
              }
            }`,
        })
        .then((result) => {
          result = result?.data?.getRedeemedPrimePerks.primePerks;

          if (result === undefined) {
            throw new Error(
              "Unable to get user's redeemed prime perks. Check the network tab for details on the API call."
            );
          }

          if (this.isCacheEnabled()) {
            WindCreekStorage.setItem("user_redeemed_prime_perks", result);
          }

          return result;
        });
    }
  }

  // Redeem some prime tokens in exchange for one or more prime perks
  static redeemPrimeTokens(redemptions, property) {
    if (!this.isLoggedIn()) {
      throw new Error("User is not logged in.");
    }

    property = this.getPropertySlugFromName(property);

    return this.windCreekClient
      .query({
        variables: {
          input: {
            location: property,
            redemptions: redemptions,
          },
        },
        query: `
          mutation RedeemPrimeTokens($input: RedeemPrimeTokensInput!) {
            redeemPrimeTokens(input: $input)
          }`,
      })
      .then((result) => {
        result = result?.data?.redeemPrimeTokens;

        if (result === undefined) {
          throw new Error(
            "Unable to redeem prime tokens. Check the network tab for details on the API call."
          );
        }

        return result;
      });
  }

  static getPropertySlugFromName(propertyName) {
    propertyName = propertyName.replace(' ', '');
    return propertyName.charAt(0).toLowerCase() + propertyName.slice(1);
  }

  static setEmail(email) {
    if (!this.isLoggedIn()) {
      throw new Error("User is not logged in.");
    }

    return this.windCreekClient
      .query({
        operationName: "MyChangeEmail",
        variables: {
          changeEmailInput: {
            email: email,
          },
        },
        query: `
        mutation MyChangeEmail($changeEmailInput: ChangeEmailInput!) {
          changeEmail(input: $changeEmailInput) {
            success
            message
          }
        }`,
      })
      .then((result) => {
        result = result?.data?.changeEmail;

        if (result === undefined) {
          throw new Error(
            "Set email failed. Check the network tab for details on the API call."
          );
        }

        return result;
      });
  }

  static setDisplayName(name) {
    if (!this.isLoggedIn()) {
      throw new Error("User is not logged in.");
    }

    return this.windCreekClient
      .query({
        operationName: "MyChangeDisplayName",
        variables: {
          changeDisplayNameInput: {
            displayName: name,
          },
        },
        query: `
        mutation MyChangeDisplayName($changeDisplayNameInput: ChangeDisplayNameInput!) {
          changeDisplayName(input: $changeDisplayNameInput) {
            success
            message
          }
        }`,
      })
      .then((result) => {
        result = result?.data?.changeDisplayName;

        if (result === undefined) {
          throw new Error(
            "Set display name failed. Check the network tab for details on the API call."
          );
        }

        return result;
      });
  }

  static setPreferredLocation(location) {
    if (!this.isLoggedIn()) {
      throw new Error("User is not logged in.");
    }

    return this.windCreekClient
      .query({
        operationName: "MyChangePreferredLocation",
        variables: {
          changePreferredLocation: {
            preferredLocation: location,
          },
        },
        query: `
        mutation MyChangePreferredLocation($changePreferredLocation: ChangePreferredLocation!) {
          changePreferredLocation(input: $changePreferredLocation) {
            user {
              ...UserFields
            }
          }
        }

        fragment UserFields on User {
          userId
          email
          displayName
          preferredLocation
          preferredLanguage
          avatarUrl
          isLockedOut
          isWallBanned
          creationDate
          roles
        }`,
      })
      .then((result) => {
        result = result?.data?.changePreferredLocation?.user;

        if (result === undefined) {
          throw new Error(
            "Set preferred location failed. Check the network tab for details on the API call."
          );
        }

        return result;
      });
  }

  static setProfileImage(image) {
    if (!this.isLoggedIn()) {
      throw new Error("User is not logged in.");
    }

    return this.windCreekClient
      .uploadImage(image, "uploadAvatarImage")
      .then((result) => {
        if (result?.imageKey) {
          return this.changeAvatar(result.imageKey);
        } else {
          throw new Error(
            "Profile image upload failed. Check the network tab for details on the API call."
          );
        }
      });
  }

  static changeAvatar(imageKey) {
    if (!this.isLoggedIn()) {
      throw new Error("User is not logged in.");
    }

    return this.windCreekClient
      .query({
        operationName: "MyChangeAvatar",
        variables: {
          changeAvatarInput: {
            avatar: imageKey,
          },
        },
        query: `
        mutation MyChangeAvatar($changeAvatarInput: ChangeAvatarInput!) {
          changeAvatar(input: $changeAvatarInput) {
            success
            message
            user {
              avatarUrl
            }
          }
        }`,
      })
      .then((result) => {
        result = result?.data?.changeAvatar;

        if (result === undefined) {
          throw new Error(
            "Set avatar image failed. Check the network tab for details on the API call."
          );
        }

        return result;
      });
  }

  static getTermsOfService() {
    if (
      this.isCacheEnabled() &&
      WindCreekStorage.exists("user_terms_of_service")
    ) {
      return Promise.resolve(WindCreekStorage.getItem("user_terms_of_service"));
    } else {
      return this.windCreekClient
        .query({
          query: `
        {
          termsOfServiceAsMarkdown {
            termsOfService
          }
        }`,
        })
        .then((result) => {
          result = result?.data?.termsOfServiceAsMarkdown?.termsOfService;

          if (result === undefined) {
            throw new Error(
              "Unable to get terms of service. Check the network tab for details on the API call."
            );
          }

          if (this.isCacheEnabled()) {
            WindCreekStorage.setItem("user_terms_of_service", result);
          }

          return result;
        });
    }
  }

  static acceptTermsOfService() {
    if (!this.isLoggedIn()) {
      throw new Error("User is not logged in.");
    }

    return this.windCreekClient
      .query({
        query: "mutation { acceptTermsOfService }",
      })
      .then((result) => {
        result = result?.data?.acceptTermsOfService;

        if (result === undefined) {
          throw new Error(
            "Unable to accept terms of service. Check the network tab for details on the API call."
          );
        }

        if (result === true) {
          WindCreekStorage.removeItem("user_accept_tos_required");
        }

        return result;
      });
  }

  static setStatusLoggedIn(showMessage) {
    hideElement('[data-signed-in="false"]');
    showElement('[data-signed-in="true"]');

    if (showMessage) {
      this.successfulSignInToast.show();
    }
  }

  static setStatusLoggedOut(showMessage) {
    hideElement('[data-signed-in="true"]');
    showElement('[data-signed-in="false"]');

    if (showMessage) {
      this.successfulSignOutToast.show();
    }
  }

  static setStatusMaintenanceMode() {
    if (this.maintenanceModeEnabled) {
      return;
    }

    this.maintenanceModeEnabled = true;

    this.setStatusLoggedOut(false);

    hideElement(".login__form");
    hideElement(".login__footer");
    showElement("[data-maintenance-message]");

    this.windCreekClient
      .checkMaintenanceMode()
      .then((result) => {
        let message = result?.message;
        let estimatedEnd = result?.estimatedEnd;

        if (message && message.trim() !== "") {
          message = message.replace("\n", "\n\n");
          document
            .querySelectorAll("[data-maintenance-mode-message-text]")
            .forEach((element) => {
              element.innerText = message;
            });
        }

        if (estimatedEnd) {
          estimatedEnd = estimatedEnd.replace(" ", "T") + "Z";
          let estimatedEndObj = new Date(estimatedEnd);
          let formattedEstimatedEnd =
            estimatedEndObj.toLocaleDateString() +
            " " +
            estimatedEndObj.toLocaleTimeString([], {
              hour: "numeric",
              minute: "2-digit",
            });
          document
            .querySelectorAll("[data-maintenance-completion-time-value]")
            .forEach((element) => {
              element.innerText = formattedEstimatedEnd;
            });
          showElement("[data-maintenance-completion-time]");
        }
      })
      .finally(() => {
        let maintenanceMessageDialog = document.querySelector(
          '[data-dialog="dialog-maintenance-message"]'
        );
        if (maintenanceMessageDialog) {
          // TODO: replace this: toggleDialog('dialog-maintenance-message');
        }
      });
  }

  static setStatusNeedsToAcceptTOS() {
    this.setStatusLoggedOut();
    hideElement("[data-user-login-form]");
    hideElement("[data-toggle-login-type]");
    showElement("[data-terms-of-service-form]");
  }

  static needsToAcceptTOS() {
    return WindCreekStorage.exists("user_accept_tos_required");
  }
}
